import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Row, Col, Card, CardBody, CardTitle, Badge, Button, Table } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { StatusBadge } from '../../components/StatusBadge';
import { RelativeTime } from '../../components/RelativeTime';
import { CopyChip } from '../../components/CopyChip';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { infraFixture } from './fixture';

export function InfrastructurePage() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  const query = useQuery({
    queryKey: ['infra', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return infraFixture();
      }
      return fetch('/v1/admin/infra');
    },
    refetchInterval: 15_000,
  });

  if (query.isLoading) {
    return (
      <div>
        <PageHeader title="Infrastructure" />
        <QueryLoadingState />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div>
        <PageHeader title="Infrastructure" />
        <QueryErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  const data = query.data;

  return (
    <div>
      <PageHeader
        title="Infrastructure"
        subtitle="System-Health · GET /v1/admin/infra · Auto-Refresh 15s"
        actions={
          <Button color="primary" outline size="sm" onClick={() => query.refetch()}>
            ↻ Aktualisieren
          </Button>
        }
      />

      <Row className="g-3 mb-3">
        <Col md="6">
          <PingCard
            title="PostgreSQL"
            ok={data.db.ok}
            latencyMs={data.db.latencyMs}
            details={[
              ['Version', data.db.version],
              ['Pool', `${data.db.pool.active} aktiv / ${data.db.pool.idle} idle / ${data.db.pool.total} total`],
            ]}
          />
        </Col>
        <Col md="6">
          <PingCard
            title="Redis"
            ok={data.redis.ok}
            latencyMs={data.redis.latencyMs}
            details={[['Version', data.redis.version]]}
          />
        </Col>
      </Row>

      <Card className="mb-3">
        <CardBody>
          <CardTitle tag="h5">BullMQ-Queues</CardTitle>
          <Table size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Queue</th>
                <th className="text-end">Waiting</th>
                <th className="text-end">Active</th>
                <th className="text-end">Failed</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.queues.map((q) => (
                <tr key={q.name}>
                  <td><code>{q.name}</code></td>
                  <td className="text-end">{q.waiting}</td>
                  <td className="text-end">{q.active}</td>
                  <td className="text-end">
                    {q.failed > 0 ? (
                      <Badge color="danger" pill>{q.failed}</Badge>
                    ) : (
                      <span className="text-muted">0</span>
                    )}
                  </td>
                  <td>
                    {q.failed > 0 && <Badge color="warning" pill className="small">Aufmerksamkeit</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      <Row className="g-3">
        <Col md="6">
          <Card className="h-100">
            <CardBody>
              <CardTitle tag="h5">Worker-Heartbeats</CardTitle>
              <ul className="list-group list-group-flush">
                {data.workers.map((w) => (
                  <li
                    key={w.id}
                    className="list-group-item d-flex justify-content-between align-items-center px-0"
                  >
                    <div>
                      <div className="fw-semibold small">{w.id} <span className="text-muted">· pid {w.pid}</span></div>
                      <div className="small text-muted">
                        Heartbeat: <RelativeTime iso={w.heartbeatAt} />
                      </div>
                    </div>
                    <StatusBadge value={w.status === 'healthy' ? 'healthy' : 'warning'} />
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card className="h-100">
            <CardBody>
              <CardTitle tag="h5">Build-Version</CardTitle>
              <dl className="row small mb-0">
                <dt className="col-sm-4 text-muted text-uppercase">Version</dt>
                <dd className="col-sm-8 fw-semibold">{data.build.version}</dd>
                <dt className="col-sm-4 text-muted text-uppercase">SHA</dt>
                <dd className="col-sm-8"><CopyChip value={data.build.sha} label="Commit-SHA" /></dd>
                <dt className="col-sm-4 text-muted text-uppercase">Gebaut</dt>
                <dd className="col-sm-8"><RelativeTime iso={data.build.builtAt} /></dd>
                <dt className="col-sm-4 text-muted text-uppercase">Node</dt>
                <dd className="col-sm-8"><code>{data.build.node}</code></dd>
              </dl>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <p className="text-muted small mt-3 mb-0 text-end">
        Stand: <RelativeTime iso={data.checkedAt} />
      </p>
    </div>
  );
}

function PingCard({ title, ok, latencyMs, details }) {
  return (
    <Card className="h-100">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <CardTitle tag="h5" className="mb-0">{title}</CardTitle>
          {ok ? (
            <Badge color="success" pill>OK · {latencyMs} ms</Badge>
          ) : (
            <Badge color="danger" pill>DOWN</Badge>
          )}
        </div>
        <dl className="row small mb-0">
          {details.map(([k, v]) => (
            <Fragment key={k}>
              <dt className="col-sm-4 text-muted text-uppercase">{k}</dt>
              <dd className="col-sm-8">{v}</dd>
            </Fragment>
          ))}
        </dl>
      </CardBody>
    </Card>
  );
}

